import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import Glp1QuizPage from '@/components/intake/glp1-quiz/Glp1QuizPage'
import { getIntakePrefillData } from '@/services/intakeService'
import { API_BASE_URL } from '@/lib/apiConfig';

export const Glp1QuizIntakeForm: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(true)
  const [initialAnswers, setInitialAnswers] = useState<Record<string, unknown>>({})
  const [initialStageId, setInitialStageId] = useState<string | undefined>(undefined)
  const [initialCompletedAtByStage, setInitialCompletedAtByStage] = useState<Record<string, string>>({})

  const progressStorageKey = token ? `clinic_glp1_progress_${token}` : undefined

  const parsePrefillHeight = (heightValue: unknown, heightFeetValue: unknown, heightInchesValue: unknown) => {
    if (typeof heightValue === 'string') {
      const trimmed = heightValue.trim()
      if (!trimmed) return { feet: undefined, inches: undefined }

      const feetInchesMatch = trimmed.match(/(\d{1,2})\s*(?:'|ft|feet|foot)\s*(\d{1,2})?/i)
      if (feetInchesMatch) {
        const parsedFeet = Number.parseInt(feetInchesMatch[1], 10)
        const parsedInches = feetInchesMatch[2] ? Number.parseInt(feetInchesMatch[2], 10) : 0
        return {
          feet: parsedFeet,
          inches: Math.max(0, Math.min(11, parsedInches)),
        }
      }

      const asNumber = Number(trimmed)
      if (Number.isFinite(asNumber) && asNumber > 11) {
        const rounded = Math.round(asNumber)
        return { feet: Math.floor(rounded / 12), inches: rounded % 12 }
      }
    }

    const fromExplicitFeet = Number(heightFeetValue)
    const fromExplicitInches = Number(heightInchesValue)
    if (Number.isFinite(fromExplicitFeet) && fromExplicitFeet > 0) {
      return {
        feet: Math.floor(fromExplicitFeet),
        inches: Number.isFinite(fromExplicitInches) ? Math.max(0, Math.min(11, Math.floor(fromExplicitInches))) : 0,
      }
    }

    if (typeof heightValue === 'number' && Number.isFinite(heightValue)) {
      const rounded = Math.max(0, Math.round(heightValue))
      if (rounded > 11) {
        return { feet: Math.floor(rounded / 12), inches: rounded % 12 }
      }
    }

    return { feet: undefined, inches: undefined }
  }

  const parsePrefillWeight = (weightValue: unknown) => {
    if (typeof weightValue === 'number' && Number.isFinite(weightValue)) return Math.round(weightValue)
    if (typeof weightValue === 'string') {
      const match = weightValue.match(/\d+(\.\d+)?/)
      if (match) return Math.round(Number.parseFloat(match[0]))
    }
    return undefined
  }

  const parsePrefillMedications = (value: unknown): string[] => {
    if (!value) return []
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean)
    }
    if (typeof value !== 'string') return []

    const trimmed = value.trim()
    if (!trimmed) return []

    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => (typeof item === 'string' ? item.trim() : ''))
            .filter(Boolean)
        }
      } catch {
        // Fallback to delimiter parsing below.
      }
    }

    return trimmed
      .split(/[,\n;]/)
      .map((item) => item.trim())
      .filter(Boolean)
  }

  const parsePrefillAllergies = (value: unknown): string => {
    if (!value) return ''
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean)
        .join(', ')
    }
    if (typeof value === 'string') return value.trim()
    return ''
  }

  useEffect(() => {
    const loadPrefill = async () => {
      if (!token) return
      try {
        const prefill = await getIntakePrefillData(token)
        const prefillData = prefill?.data || {}
        const rawDob = prefillData.dob
        const normalizedDob =
          typeof rawDob === 'string' && rawDob.length >= 10
            ? rawDob.slice(0, 10)
            : ''
        const rawSex = (
          prefillData.biological_sex ||
          prefillData.gender ||
          ''
        )
          .toString()
          .trim()
          .toLowerCase()
        const normalizedSex =
          rawSex === 'm' || rawSex === 'male'
            ? 'male'
            : rawSex === 'f' || rawSex === 'female'
              ? 'female'
              : ''
        const { feet: heightFeet, inches: heightInches } = parsePrefillHeight(
          prefillData.height,
          prefillData.heightFeet,
          prefillData.heightInches
        )
        const weightLbs = parsePrefillWeight(prefillData.weight)
        const prefillMedications = parsePrefillMedications(
          prefillData.currentMedications || prefillData.medications
        )
        const prefillAllergies = parsePrefillAllergies(prefillData.allergies)
        const prefillAnswers = {
          intake_token: token,
          first_name: prefillData.first_name || '',
          middle_name: prefillData.middle_name || '',
          last_name: prefillData.last_name || '',
          email: prefillData.email || '',
          phone: prefillData.phone || '',
          date_of_birth: normalizedDob,
          sex_at_birth: normalizedSex,
          ...(typeof heightFeet === 'number' ? { height_feet: heightFeet } : {}),
          ...(typeof heightInches === 'number' ? { height_inches: heightInches } : {}),
          ...(typeof weightLbs === 'number' ? { weight_lbs: weightLbs } : {}),
          ...((typeof heightFeet === 'number' || typeof heightInches === 'number' || typeof weightLbs === 'number')
            ? {
                bmi_data: {
                  ...(typeof heightFeet === 'number' ? { height_feet: heightFeet } : {}),
                  ...(typeof heightInches === 'number' ? { height_inches: heightInches } : {}),
                  ...(typeof weightLbs === 'number' ? { weight_lbs: weightLbs } : {}),
                },
              }
            : {}),
          ...(prefillMedications.length
            ? {
                medication_data: { medications: prefillMedications },
              }
            : {}),
          ...(prefillAllergies
            ? {
                medication_allergies: prefillAllergies,
              }
            : {}),
          // Billing/shipping address (pre-fill from patient address)
          ...((prefillData.billing_address_line_1 || prefillData.address_line_1 || prefillData.city || prefillData.state || prefillData.zip_code)
            ? (() => {
                const bLine1 = prefillData.billing_address_line_1 || prefillData.address_line_1 || ''
                const bLine2 = prefillData.billing_address_line_2 || prefillData.address_line_2 || ''
                const bCity = prefillData.billing_city || prefillData.city || ''
                const bState = prefillData.billing_state || prefillData.state || ''
                const bZip = prefillData.billing_zip_code || prefillData.zip_code || ''
                const sLine1 = prefillData.shipping_address_line_1 || bLine1
                const sLine2 = prefillData.shipping_address_line_2 || bLine2
                const sCity = prefillData.shipping_city || bCity
                const sState = prefillData.shipping_state || bState
                const sZip = prefillData.shipping_zip_code || bZip
                const addressesMatch =
                  sLine1 === bLine1 && sLine2 === bLine2 && sCity === bCity && sState === bState && sZip === bZip
                return {
                  shipping_billing_data: {
                    billingAddressLine1: bLine1,
                    billingAddressLine2: bLine2,
                    billingCity: bCity,
                    billingState: bState,
                    billingPostalCode: bZip,
                    shippingAddressLine1: sLine1,
                    shippingAddressLine2: sLine2,
                    shippingCity: sCity,
                    shippingState: sState,
                    shippingPostalCode: sZip,
                    billingSameAsShipping: addressesMatch,
                  },
                }
              })()
            : {}),
        }

        let savedAnswers: Record<string, unknown> = {}
        let savedStageId: string | undefined
        let savedCompletedAtByStage: Record<string, string> = {}

        if (progressStorageKey) {
          try {
            const saved = localStorage.getItem(progressStorageKey)
            if (saved) {
              const parsed = JSON.parse(saved) as {
                answers?: Record<string, unknown>
                stageId?: string
                completedAtByStage?: Record<string, string>
              }
              savedAnswers = parsed?.answers || {}
              savedStageId = parsed?.stageId
              savedCompletedAtByStage = parsed?.completedAtByStage || {}
            }
          } catch {
            // Ignore invalid stored progress and fall back to prefill only.
          }
        }

        setInitialAnswers({ ...prefillAnswers, ...savedAnswers })
        setInitialStageId(savedStageId)
        setInitialCompletedAtByStage(savedCompletedAtByStage)
      } catch {
        toast({
          title: 'Unable to load intake',
          description: 'Please try opening the intake link again.',
          variant: 'destructive',
        })
      } finally {
        setIsLoading(false)
      }
    }
    void loadPrefill()
  }, [progressStorageKey, token, toast])

  if (!token) return null
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <Loader2 className="h-6 w-6 animate-spin text-white" />
      </div>
    )
  }

  return (
    <Glp1QuizPage
      initialAnswers={initialAnswers}
      initialStageId={initialStageId}
      initialCompletedAtByStage={initialCompletedAtByStage}
      progressStorageKey={progressStorageKey}
      onSubmit={async (payload) => {
        const apiUrl = API_BASE_URL
        // Extract payment method data included by the workflow hook
        const payloadObj = payload as {
          stages?: unknown[]
          payment_method_data?: { paymentMethodId?: string; setupIntentId?: string }
        }
        const paymentData = payloadObj.payment_method_data
        const response = await fetch(`${apiUrl}/intakes/submit/${token}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            responses: payload,
            therapyCategory: 'glp1',
            ...(paymentData?.paymentMethodId && { paymentMethodId: paymentData.paymentMethodId }),
            ...(paymentData?.setupIntentId && { setupIntentId: paymentData.setupIntentId }),
          }),
        })
        const data = await response.json()
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || 'Submission failed')
        }
        if (progressStorageKey) {
          try {
            localStorage.removeItem(progressStorageKey)
          } catch {
            // Ignore storage cleanup failure.
          }
        }
      }}
    />
  )
}

export default Glp1QuizIntakeForm
